import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { IconButton } from "./IconButton";
import { cn } from "@/lib/cn";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Optional footer (actions). */
  footer?: ReactNode;
  /** Max width preset. */
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

// Effective width is min(100vw-2rem, max-w) — raising the large presets only widens on big screens;
// the sm/md confirmation dialogs and any phone-width rendering are unchanged. lg/xl were too narrow on
// wide screens (table headers + pills wrapped), so they are bumped up a step.
const SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
} as const;

/**
 * Native <dialog> modal. Using the platform dialog means it renders in the top layer — it ESCAPES any
 * ancestor's overflow:hidden / transform / z-index stacking (the classic portal-clipping bug) without
 * a manual React portal. Backdrop + Escape close are native; we add focus management + the close
 * button. Motion respects prefers-reduced-motion via the token durations.
 *
 * r12 22/7 (principal feedback, prod screenshot — PeriodModal forcing a scroll the moment its date-range
 * popover opened): a native <dialog> has NO built-in height cap — left alone, its box is "fit-content",
 * unbounded, and a tall form plus an open calendar popover can render past the viewport edge with no way
 * to reach it (top-layer boxes don't hand that overflow to the page's own scrollbar). Fix is structural,
 * not per-modal: the dialog is now a flex column capped at 85vh, with the header/footer pinned
 * (shrink-0) and ONLY the body scrolling. DateRangePicker's own popover (absolute-positioned, viewport-
 * aware flip-to-top — see its own layout effect) already keeps itself inside `window.innerHeight`; capping
 * the dialog at 85vh keeps "the visible scroll area" and "the viewport" close enough in practice that its
 * flip decision and this dialog's own scroll extent agree, so the calendar lands somewhere the operator
 * can actually see without a manual scroll on a typical laptop (~800px tall) viewport.
 *
 * The dialog's display MUST stay gated on [open] (`hidden open:flex`, never a bare `flex`): a closed
 * <dialog> is only hidden by the UA's `dialog:not([open]) { display: none }`, and ANY author display
 * utility overrides UA rules — an unconditional `flex` makes every closed modal render in-flow as an
 * empty header box with a stray × button (r13 prod regression on Home).
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
  className,
}: ModalProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  // Native dialog fires `cancel` (Escape) and `close`; route both to onClose so React state stays in sync.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => onClose();
    dialog.addEventListener("cancel", handle);
    dialog.addEventListener("close", handle);
    return () => {
      dialog.removeEventListener("cancel", handle);
      dialog.removeEventListener("close", handle);
    };
  }, [onClose]);

  // Click on the backdrop (the dialog element itself, outside the inner panel) closes.
  function onClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) onClose();
  }

  return (
    <dialog
      ref={ref}
      onClick={onClick}
      aria-labelledby={titleId}
      className={cn(
        "m-auto hidden max-h-[85vh] w-[calc(100vw-2rem)] flex-col rounded-(--radius-lg) border border-(--border) " +
          "bg-(--surface-raised) p-0 text-(--text) shadow-(--shadow-lg) " +
          "backdrop:bg-[oklch(0.3_0.02_265_/_0.4)] backdrop:backdrop-blur-sm " +
          "open:flex open:animate-[modal-in_var(--dur)_var(--ease-out)] " +
          "motion-reduce:open:animate-none",
        SIZES[size],
        className,
      )}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-(--border-hairline) px-5 py-4">
        <h2 id={titleId} className="text-(length:--text-lg) font-semibold text-(--text-strong)">
          {title}
        </h2>
        <IconButton
          label={t("a11y.closeDialog")}
          icon={<X className="size-4" />}
          size="sm"
          onClick={onClose}
        />
      </div>
      <div className="overflow-y-auto px-5 py-4">{children}</div>
      {footer && (
        <div className="flex shrink-0 justify-end gap-2 border-t border-(--border-hairline) bg-(--surface-sunken) px-5 py-3">
          {footer}
        </div>
      )}
    </dialog>
  );
}
